import { Component, ComponentType } from "@/types";
import type { BarChartProperties } from "@/types/chart-properties";
import { ChartPropertyChange, commonChartHandler } from "./CommonChartHandler";
import { ChartDataPoint } from "../ChartDataManager";
import { colorMixin, axisMixin, animationMixin, mergeMixinResults } from "./mixins";

/**
 * Bar chart specific command handler
 */
export class BarChartHandler {
  /**
   * Phase 1.1: Bar width preset values
   * Maps named width keywords to barSize values (pixels)
   */
  private readonly barWidthPresets: Record<string, number | null> = {
    'hairline': 8,
    'thin': 15,
    'slim': 20,
    'narrow': 25,
    'normal': null,    // null means use default/auto
    'default': null,
    'wide': 45,
    'thick': 55,
    'fat': 65,
    'extra-wide': 75,
  };

  /**
   * Phase 1.1: Bar stroke width presets (for borders)
   */
  private readonly strokeWidthPresets: Record<string, number> = {
    'hairline': 0.5,
    'thin': 1,
    'light': 1.5,
    'normal': 2,
    'default': 2,
    'medium': 2.5,
    'thick': 3,
    'heavy': 4,
    'bold': 4,
    'extra-thick': 5,
  };

  /**
   * Phase 1.2: Stroke style presets (for bar borders)
   */
  private readonly strokeStylePresets: Record<string, string> = {
    'solid': '',
    'continuous': '',
    'dashed': '5 5',
    'dotted': '2 2',
    'dot': '2 2',
    'long-dash': '10 5',
    'short-dash': '3 3',
    'dash-dot': '5 2 2 2',
    'dash-dot-dot': '5 2 2 2 2 2',
    'sparse': '15 10',
    'dense': '2 1',
  };

  /**
   * Parse bar chart specific commands
   */
  parseCommand(command: string, component: Component): ChartPropertyChange | null {
    // First try common commands
    const commonChanges = commonChartHandler.parseCommand(command, component);

    // Apply mixins
    const colorChanges = colorMixin.parseCommand(command, component);
    const axisChanges = axisMixin.parseCommand(command, component);
    const animationChanges = animationMixin.parseCommand(command, component);

    // Then apply bar-specific commands
    const barChanges = this.parseBarSpecificCommand(command, component);

    // Merge all changes
    const mixinChanges = mergeMixinResults(colorChanges, axisChanges, animationChanges);

    if (commonChanges || mixinChanges || barChanges) {
      return {
        ...commonChanges,
        properties: {
          ...commonChanges?.properties,
          ...mixinChanges,
          ...barChanges?.properties,
        },
        style: {
          ...commonChanges?.style,
          ...barChanges?.style,
        },
        data: commonChanges?.data || barChanges?.data,
        type: commonChanges?.type || barChanges?.type,
      };
    }

    return null;
  }

  private parseBarSpecificCommand(command: string, component: Component): ChartPropertyChange | null {
    const lowerCommand = command.toLowerCase();
    const changes: ChartPropertyChange = {};

    // Phase 1.1: Bar width
    if (this.isBarWidthCommand(lowerCommand)) {
      const widthChanges = this.parseBarWidthCommand(command, component);
      if (widthChanges) {
        changes.properties = { ...changes.properties, ...widthChanges };
      }
    }

    // Phase 1.2: Bar stroke/border
    if (this.isBarStrokeCommand(lowerCommand)) {
      const strokeChanges = this.parseBarStrokeCommand(command, component);
      if (strokeChanges) {
        changes.properties = { ...changes.properties, ...strokeChanges };
      }
    }

    // Phase 1.3: Bar opacity
    if (this.isBarOpacityCommand(lowerCommand)) {
      const opacityChanges = this.parseBarOpacityCommand(command, component);
      if (opacityChanges) {
        changes.properties = { ...changes.properties, ...opacityChanges };
      }
    }

    // Phase 1.4: Negative value colors
    if (this.isNegativeColorCommand(lowerCommand)) {
      const negativeChanges = this.parseNegativeColorCommand(command);
      if (negativeChanges) {
        changes.properties = { ...changes.properties, ...negativeChanges };
      }
    }

    // Stack/unstack bars
    if (this.isStackCommand(lowerCommand)) {
      const stackChanges = this.parseStackCommand(command);
      if (stackChanges) {
        changes.properties = { ...changes.properties, ...stackChanges };
      }
    }

    // Bar orientation (horizontal/vertical)
    if (this.isOrientationCommand(lowerCommand)) {
      const orientationChanges = this.parseOrientationCommand(command);
      if (orientationChanges) {
        changes.properties = { ...changes.properties, ...orientationChanges };
      }
    }

    // Bar width and spacing
    if (this.isSpacingCommand(lowerCommand)) {
      const spacingChanges = this.parseSpacingCommand(command, component);
      if (spacingChanges) {
        changes.properties = { ...changes.properties, ...spacingChanges };
      }
    }

    // Bar grouping
    if (this.isGroupingCommand(lowerCommand)) {
      const groupingChanges = this.parseGroupingCommand(command);
      if (groupingChanges) {
        changes.properties = { ...changes.properties, ...groupingChanges };
      }
    }

    // Bar corner radius
    if (this.isRoundingCommand(lowerCommand)) {
      const roundingChanges = this.parseRoundingCommand(command);
      if (roundingChanges) {
        changes.properties = { ...changes.properties, ...roundingChanges };
      }
    }

    // Bar labels
    if (this.isLabelCommand(lowerCommand)) {
      const labelChanges = this.parseLabelCommand(command);
      if (labelChanges) {
        changes.properties = { ...changes.properties, ...labelChanges };
      }
    }

    // Multiple bar series
    if (this.isMultiSeriesCommand(lowerCommand)) {
      const multiSeriesChanges = this.parseMultiSeriesCommand(command, component);
      if (multiSeriesChanges) {
        changes.properties = { ...changes.properties, ...multiSeriesChanges.properties };
        if (multiSeriesChanges.data) {
          changes.data = multiSeriesChanges.data;
        }
      }
    }

    // Waterfall chart mode
    if (this.isWaterfallCommand(lowerCommand)) {
      const waterfallChanges = this.parseWaterfallCommand(command);
      if (waterfallChanges) {
        changes.properties = { ...changes.properties, ...waterfallChanges };
      }
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isStackCommand(command: string): boolean {
    return /stack|cumulative|pile|layer/.test(command);
  }

  private parseStackCommand(command: string): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};

    if (command.includes("stack") || command.includes("cumulative") || command.includes("pile")) {
      if (command.includes("unstack") || command.includes("separate") || command.includes("split")) {
        changes.stacked = false;
      } else {
        changes.stacked = true;

        // Stack type
        if (command.includes("percentage") || command.includes("percent") || command.includes("100%")) {
          changes.stackType = "percentage";
        } else if (command.includes("relative")) {
          changes.stackType = "relative";
        } else {
          changes.stackType = "normal";
        }
      }
    }

    // Group bars instead of stacking
    if (command.includes("group") && command.includes("bar")) {
      changes.stacked = false;
      changes.grouped = true;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isOrientationCommand(command: string): boolean {
    return /horizontal|vertical|rotate|flip|sideways|column/.test(command);
  }

  private parseOrientationCommand(command: string): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};

    if (command.includes("horizontal") || command.includes("sideways") || command.includes("flip")) {
      changes.layout = "horizontal";
      // Swap axis configurations for horizontal layout
      changes.swapAxes = true;
    } else if (command.includes("vertical") || command.includes("column") || command.includes("upright")) {
      changes.layout = "vertical";
      changes.swapAxes = false;
    }

    // Support for rotating the entire chart
    if (command.includes("rotate") && (command.includes("90") || command.includes("ninety"))) {
      changes.layout = changes.layout === "horizontal" ? "vertical" : "horizontal";
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isSpacingCommand(command: string): boolean {
    return /spacing|gap|width|narrow|wide|thick|thin|compact|spread/.test(command);
  }

  private parseSpacingCommand(command: string, component: Component): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};
    const currentGap = typeof component.properties?.barGap === 'number'
      ? component.properties.barGap
      : 4;
    const currentCategoryGap = typeof component.properties?.barCategoryGap === 'number'
      ? component.properties.barCategoryGap
      : 20;

    // Specific gap values
    const gapMatch = command.match(/(?:bar\s+)?gap\s+(?:of\s+)?(\d+)/i);
    if (gapMatch) {
      changes.barGap = parseInt(gapMatch[1]);
    }

    const categoryGapMatch = command.match(/category\s+gap\s+(?:of\s+)?(\d+)/i);
    if (categoryGapMatch) {
      changes.barCategoryGap = parseInt(categoryGapMatch[1]);
    }

    // Width adjustments
    const widthMatch = command.match(/bar\s+width\s+(?:of\s+)?(\d+(?:\.\d+)?)\s*(%|px)?/i);
    if (widthMatch) {
      let width = parseFloat(widthMatch[1]);
      if (widthMatch[2] === '%') {
        changes.barWidthRatio = width / 100;
      } else {
        changes.barWidth = width;
      }
    }

    // Relative adjustments
    if (command.includes("wider") || command.includes("thicker") || command.includes("fatter")) {
      changes.barWidthRatio = 0.8;
    } else if (command.includes("narrower") || command.includes("thinner") || command.includes("slimmer")) {
      changes.barWidthRatio = 0.4;
    } else if (command.includes("wide")) {
      changes.barWidthRatio = 0.7;
    } else if (command.includes("narrow")) {
      changes.barWidthRatio = 0.5;
    }

    // Spacing presets
    if (command.includes("more space") || command.includes("increase spacing") || command.includes("spread")) {
      changes.barGap = currentGap + 4;
      changes.barCategoryGap = currentCategoryGap + 10;
    } else if (command.includes("less space") || command.includes("decrease spacing") || command.includes("compact")) {
      changes.barGap = Math.max(0, currentGap - 2);
      changes.barCategoryGap = Math.max(5, currentCategoryGap - 10);
    } else if (command.includes("no gap") || command.includes("no space")) {
      changes.barGap = 0;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isGroupingCommand(command: string): boolean {
    return /group|cluster|separate|split|side by side/.test(command);
  }

  private parseGroupingCommand(command: string): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};

    if (command.includes("group") || command.includes("cluster") || command.includes("side by side")) {
      changes.grouped = true;
      changes.stacked = false;

      // Parse number of bars per group
      const groupCountMatch = command.match(/(\d+)\s+bars?\s+per\s+group/i);
      if (groupCountMatch) {
        changes.barsPerGroup = parseInt(groupCountMatch[1]);
      }
    }

    if (command.includes("separate") || command.includes("split")) {
      changes.grouped = true;
      changes.stacked = false;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isRoundingCommand(command: string): boolean {
    return /round|curve|corner|smooth|sharp|square/.test(command);
  }

  private parseRoundingCommand(command: string): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};

    // Specific radius value
    const radiusMatch = command.match(/(?:corner\s+)?radius\s+(?:of\s+)?(\d+)/i);
    if (radiusMatch) {
      changes.cornerRadius = parseInt(radiusMatch[1]);
    } else if (command.includes("round") || command.includes("curved")) {
      if (command.includes("very") || command.includes("fully")) {
        changes.cornerRadius = 10;
      } else if (command.includes("slightly") || command.includes("subtle")) {
        changes.cornerRadius = 2;
      } else {
        changes.cornerRadius = 5;
      }
    } else if (command.includes("sharp") || command.includes("square") || command.includes("no round")) {
      changes.cornerRadius = 0;
    }

    // Top corners only (for upward bars)
    if (command.includes("top") && command.includes("corner")) {
      changes.cornerRadiusTop = true;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isLabelCommand(command: string): boolean {
    return /label|value|text|show.*number|display.*value/.test(command);
  }

  private parseLabelCommand(command: string): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};

    // Show/hide labels
    if (command.includes("show") && (command.includes("label") || command.includes("value"))) {
      changes.showLabels = true;

      // Label position
      if (command.includes("inside") || command.includes("center")) {
        changes.labelPosition = "inside";
      } else if (command.includes("outside") || command.includes("above") || command.includes("top")) {
        changes.labelPosition = "top";
      } else if (command.includes("end")) {
        changes.labelPosition = "end";
      }
    } else if (command.includes("hide") && (command.includes("label") || command.includes("value"))) {
      changes.showLabels = false;
    }

    // Label format
    if (command.includes("percentage") || command.includes("percent")) {
      changes.labelFormat = "percentage";
    } else if (command.includes("currency") || command.includes("dollar")) {
      changes.labelFormat = "currency";
    } else if (command.includes("abbreviated") || command.includes("k suffix")) {
      changes.labelFormat = "abbreviated";
    }

    // Label angle
    const angleMatch = command.match(/label.*angle\s+(?:of\s+)?(-?\d+)/i);
    if (angleMatch) {
      changes.labelAngle = parseInt(angleMatch[1]);
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private isMultiSeriesCommand(command: string): boolean {
    return /add.*series|another.*bar|multiple.*data|compare/.test(command);
  }

  private parseMultiSeriesCommand(command: string, component: Component): ChartPropertyChange | null {
    const props: Partial<BarChartProperties> = {};
    const changes: ChartPropertyChange = { properties: props };
    const rawData = component.properties?.data;
    const currentData = Array.isArray(rawData)
      ? rawData.filter((d): d is ChartDataPoint =>
          d !== null && typeof d === 'object' && !Array.isArray(d) && 'name' in d)
      : [];
    const rawBarKeys = component.properties?.barKeys;
    const barKeys = Array.isArray(rawBarKeys)
      ? rawBarKeys.filter((k): k is string => typeof k === 'string')
      : ["value"];

    // Add another bar series
    if (command.includes("add") && (command.includes("series") || command.includes("bar"))) {
      // Parse series name
      const nameMatch = command.match(/(?:series|bar)\s+(?:for|called|named)\s+["']([^"']+)["']/i) ||
                       command.match(/["']([^"']+)["']\s+(?:series|bar)/i);
      const seriesName = nameMatch ? nameMatch[1] : `Series ${barKeys.length + 1}`;
      const seriesKey = seriesName.toLowerCase().replace(/\s+/g, "_");

      // Add to barKeys
      if (!barKeys.includes(seriesKey)) {
        props.barKeys = [...barKeys, seriesKey];

        // Add sample data for the new series
        if (currentData.length > 0) {
          changes.data = currentData.map(point => ({
            ...point,
            [seriesKey]: Math.floor(Math.random() * 100) + 20,
          }));
        }
      }
    }

    // Comparison data
    if (command.includes("compare")) {
      const compareMatch = command.match(/compare\s+["']([^"']+)["']\s+(?:with|and|to)\s+["']([^"']+)["']/i);
      if (compareMatch) {
        const series1 = compareMatch[1].toLowerCase().replace(/\s+/g, "_");
        const series2 = compareMatch[2].toLowerCase().replace(/\s+/g, "_");

        props.barKeys = [series1, series2];
      }
    }

    return Object.keys(props).length > 0 ? changes : null;
  }

  private isWaterfallCommand(command: string): boolean {
    return /waterfall|cascade|running total|cumulative sum/.test(command);
  }

  private parseWaterfallCommand(command: string): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};

    if (command.includes("waterfall") || command.includes("cascade")) {
      changes.chartVariant = "waterfall";
      changes.showConnectors = true;

      // Color coding for increases/decreases
      changes.positiveColor = "#10B981"; // Green for increases
      changes.negativeColor = "#EF4444"; // Red for decreases
      changes.totalColor = "#3B82F6"; // Blue for totals
    }

    if (command.includes("running total") || command.includes("cumulative")) {
      changes.showRunningTotal = true;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  // ============================================
  // Phase 1.1: Bar Width Commands
  // ============================================

  /**
   * Phase 1.1: Detect bar width commands
   */
  private isBarWidthCommand(command: string): boolean {
    // Match bar width/size commands, presets, percentage adjustments, and relative commands
    // Patterns:
    // 1. bar width/size, width/size bar
    // 2. preset before bar: thin bar, thick bars, etc.
    // 3. bar before preset: bars thick, bar thin, etc.
    // 4. numeric: 40px wide bars
    // 5. percentage: 50% wider
    // 6. double/halve
    // 7. relative: make bars wider
    return /bar.*(?:width|size)|(?:width|size).*bar|(?:thin|thick|narrow|wide|fat|slim|hairline|extra-wide).*bars?|bars?\s+(?:thin|thick|narrow|wide|fat|slim|hairline|extra-wide)|\d+\s*(?:px)?\s+(?:wide|bars?)|\d+%\s+(?:wider|thicker|narrower|thinner)|(?:double|halve).*(?:bar|width)|(?:make\s+)?bars?\s+(?:wider|narrower|thinner|thicker|fatter|slimmer)/.test(command);
  }

  /**
   * Phase 1.1: Parse bar width commands
   */
  private parseBarWidthCommand(command: string, component: Component): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};
    const currentWidth = typeof component.properties?.barSize === 'number'
      ? component.properties.barSize
      : 30;

    console.log(`[Phase 1.1] Parsing bar width command: "${command}", current: ${currentWidth}`);

    // Pattern 1: Specific numeric width (check FIRST before presets to avoid "40px wide" matching "wide" preset)
    const widthMatch = command.match(/(?:bar\s+)?(?:width|size)\s+(?:of\s+|to\s+)?(\d+)/i) ||
                       command.match(/(\d+)\s*(?:px)?\s+(?:wide|bars?)/i);
    if (widthMatch) {
      changes.barSize = Math.max(5, Math.min(100, parseInt(widthMatch[1])));
      console.log(`[Phase 1.1] Numeric width: ${changes.barSize}`);
      return changes;
    }

    // Pattern 2: Named width presets
    // Match patterns like: "thin bars", "make bars thick", "use hairline bars", "bars thick"
    // Use word boundary \b to avoid matching "wide" in "wider" or "thick" in "thicker"
    const presetMatch = command.match(/(?:use\s+|make\s+|set\s+)?(?:bars?\s+)?\b(hairline|thin|slim|narrow|normal|default|wide|thick|fat|extra-wide)\b(?:\s+(?:bars?|width))?/i) ||
                        command.match(/(?:make\s+)?bars?\s+\b(hairline|thin|slim|narrow|normal|default|wide|thick|fat|extra-wide)\b/i);
    if (presetMatch) {
      const presetName = presetMatch[1].toLowerCase();
      if (this.barWidthPresets[presetName] !== undefined) {
        const presetValue = this.barWidthPresets[presetName];
        if (presetValue !== null) {
          changes.barSize = presetValue;
        } else {
          changes.barSize = undefined;
        }
        console.log(`[Phase 1.1] Preset width "${presetName}": ${changes.barSize}`);
        return changes;
      }
    }

    // Pattern 3: Percentage adjustment (e.g., "50% wider", "make bars 50% thicker")
    const percentMatch = command.match(/(\d+)\s*%\s+(wider|thicker|narrower|thinner)/i) ||
                         command.match(/(?:make\s+)?bars?\s+(\d+)\s*%\s+(wider|thicker|narrower|thinner)/i);
    if (percentMatch) {
      const percent = parseInt(percentMatch[1]) / 100;
      const isWider = percentMatch[2].toLowerCase().includes('wider') || percentMatch[2].toLowerCase().includes('thicker');
      const multiplier = isWider ? (1 + percent) : (1 - percent);
      changes.barSize = Math.max(5, Math.min(100, Math.round(currentWidth * multiplier)));
      console.log(`[Phase 1.1] Percentage adjustment: ${percentMatch[1]}% ${percentMatch[2]} -> ${changes.barSize}`);
      return changes;
    }

    // Pattern 4: Double/halve
    const doubleHalveMatch = command.match(/\b(double|halve)\b.*(?:bar|width)/i);
    if (doubleHalveMatch) {
      const multiplier = doubleHalveMatch[1].toLowerCase() === 'double' ? 2 : 0.5;
      changes.barSize = Math.max(5, Math.min(100, Math.round(currentWidth * multiplier)));
      console.log(`[Phase 1.1] ${doubleHalveMatch[1]} width: ${changes.barSize}`);
      return changes;
    }

    // Pattern 5: Relative (wider/narrower) - only if "bar(s)" is present in command
    const hasBarKeyword = /bars?/i.test(command);
    if (hasBarKeyword) {
      if (command.includes("much wider") || command.includes("way wider")) {
        changes.barSize = Math.min(currentWidth + 20, 100);
      } else if (/bars?\s+(?:wider|thicker|fatter)/i.test(command) || /(?:wider|thicker|fatter)\s+bars?/i.test(command) || (command.includes("bars") && (command.includes("wider") || command.includes("thicker") || command.includes("fatter")))) {
        changes.barSize = Math.min(currentWidth + 10, 100);
      } else if (command.includes("much narrower") || command.includes("way narrower")) {
        changes.barSize = Math.max(currentWidth - 20, 5);
      } else if (/bars?\s+(?:narrower|thinner|slimmer)/i.test(command) || /(?:narrower|thinner|slimmer)\s+bars?/i.test(command) || (command.includes("bars") && (command.includes("narrower") || command.includes("thinner") || command.includes("slimmer")))) {
        changes.barSize = Math.max(currentWidth - 10, 5);
      }
    }

    if (Object.keys(changes).length > 0) {
      console.log(`[Phase 1.1] Relative adjustment: ${changes.barSize}`);
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  // ============================================
  // Phase 1.2: Bar Stroke/Border Commands
  // ============================================

  /**
   * Phase 1.2: Detect bar stroke/border commands
   */
  private isBarStrokeCommand(command: string): boolean {
    return /bar.*(?:stroke|border|outline|edge)|(?:stroke|border|outline|edge).*bar|dashed.*bar|dotted.*bar|solid.*bar|bar.*(?:dashed|dotted|solid)/.test(command);
  }

  /**
   * Phase 1.2: Parse bar stroke/border commands
   */
  private parseBarStrokeCommand(command: string, component: Component): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};

    console.log(`[Phase 1.2] Parsing bar stroke command: "${command}"`);

    // Pattern 1: Show/hide bar borders
    const showHideMatch = command.match(/(show|add|enable|hide|remove|disable)\s+(?:bar\s+)?(?:borders?|outlines?|strokes?|edges?)/i);
    if (showHideMatch) {
      const action = showHideMatch[1].toLowerCase();
      if (['show', 'add', 'enable'].includes(action)) {
        changes.barStrokeWidth = 2;
        const colorsRaw = component.properties?.colors;
        changes.barStroke = Array.isArray(colorsRaw) && typeof colorsRaw[0] === 'string' ? colorsRaw[0] : '#333333';
      } else {
        changes.barStrokeWidth = 0;
      }
      console.log(`[Phase 1.2] Show/hide borders: strokeWidth=${changes.barStrokeWidth}`);
      return changes;
    }

    // Pattern 2: Named stroke width presets
    const strokeWidthMatch = command.match(/(?:bar\s+)?(?:border|stroke|outline)\s+(hairline|thin|light|normal|medium|thick|heavy|bold)/i) ||
                             command.match(/(hairline|thin|thick|bold)\s+(?:bar\s+)?(?:border|stroke|outline)/i);
    if (strokeWidthMatch) {
      const presetName = strokeWidthMatch[1].toLowerCase();
      if (this.strokeWidthPresets[presetName] !== undefined) {
        changes.barStrokeWidth = this.strokeWidthPresets[presetName];
        if (!component.properties?.barStroke) {
          changes.barStroke = '#333333';
        }
        console.log(`[Phase 1.2] Preset stroke width "${presetName}": ${changes.barStrokeWidth}`);
        return changes;
      }
    }

    // Pattern 3: Numeric stroke width
    const numericMatch = command.match(/(?:bar\s+)?(?:border|stroke|outline)\s+(?:width\s+)?(?:of\s+)?(\d+(?:\.\d+)?)/i) ||
                         command.match(/(\d+(?:\.\d+)?)\s*(?:px)?\s+(?:bar\s+)?(?:border|stroke|outline)/i);
    if (numericMatch) {
      changes.barStrokeWidth = Math.max(0, Math.min(10, parseFloat(numericMatch[1])));
      if (!component.properties?.barStroke) {
        changes.barStroke = '#333333';
      }
      console.log(`[Phase 1.2] Numeric stroke width: ${changes.barStrokeWidth}`);
      return changes;
    }

    // Pattern 4: Stroke style patterns
    const styleMatch = command.match(/(?:bar\s+)?(?:border|stroke|outline)\s+(?:style\s+)?(?:to\s+)?(solid|dashed|dotted|long-dash|short-dash|dash-dot)/i) ||
                       command.match(/(solid|dashed|dotted)\s+(?:bar\s+)?(?:borders?|outlines?|strokes?)/i);
    if (styleMatch) {
      const styleName = styleMatch[1].toLowerCase();
      if (this.strokeStylePresets[styleName] !== undefined) {
        changes.barStrokeDasharray = this.strokeStylePresets[styleName];
        if (!component.properties?.barStrokeWidth || component.properties.barStrokeWidth === 0) {
          changes.barStrokeWidth = 2;
        }
        if (!component.properties?.barStroke) {
          changes.barStroke = '#333333';
        }
        console.log(`[Phase 1.2] Stroke style "${styleName}": ${changes.barStrokeDasharray}`);
        return changes;
      }
    }

    // Pattern 5: Stroke color
    const colorMatch = command.match(/(?:bar\s+)?(?:border|stroke|outline)\s+colou?r\s+(?:to\s+)?(#[0-9A-Fa-f]{3,6}|\w+)/i);
    if (colorMatch) {
      changes.barStroke = colorMatch[1];
      if (!component.properties?.barStrokeWidth || component.properties.barStrokeWidth === 0) {
        changes.barStrokeWidth = 2;
      }
      console.log(`[Phase 1.2] Stroke color: ${changes.barStroke}`);
      return changes;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  // ============================================
  // Phase 1.3: Bar Opacity Commands
  // ============================================

  /**
   * Phase 1.3: Detect bar opacity commands
   */
  private isBarOpacityCommand(command: string): boolean {
    return /bar.*(?:opacity|transparent|translucent|opaque|faded)|(?:opacity|transparent|translucent|opaque|faded).*bar/.test(command);
  }

  /**
   * Phase 1.3: Parse bar opacity commands
   */
  private parseBarOpacityCommand(command: string, component: Component): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};
    const currentOpacity = typeof component.properties?.barOpacity === 'number'
      ? component.properties.barOpacity
      : 1;

    console.log(`[Phase 1.3] Parsing bar opacity command: "${command}"`);

    // Pattern 1: Percentage opacity
    const percentMatch = command.match(/(?:bar\s+)?opacity\s+(?:to\s+)?(\d+)\s*%/i);
    if (percentMatch) {
      changes.barOpacity = Math.max(0, Math.min(1, parseInt(percentMatch[1]) / 100));
      console.log(`[Phase 1.3] Percentage opacity: ${changes.barOpacity}`);
      return changes;
    }

    // Pattern 2: Decimal opacity
    const decimalMatch = command.match(/(?:bar\s+)?opacity\s+(?:to\s+)?(0?\.\d+|1(?:\.0)?)/i);
    if (decimalMatch) {
      changes.barOpacity = Math.max(0, Math.min(1, parseFloat(decimalMatch[1])));
      console.log(`[Phase 1.3] Decimal opacity: ${changes.barOpacity}`);
      return changes;
    }

    // Pattern 3: Relative opacity (check BEFORE named presets to avoid "transparent" matching in "more transparent")
    if (/more\s+opaque/i.test(command) || /less\s+transparent/i.test(command)) {
      changes.barOpacity = Math.min(1, currentOpacity + 0.2);
      console.log(`[Phase 1.3] Relative opacity (more opaque): ${changes.barOpacity}`);
      return changes;
    } else if (/more\s+transparent/i.test(command) || /less\s+opaque/i.test(command)) {
      changes.barOpacity = Math.max(0.1, currentOpacity - 0.2);
      console.log(`[Phase 1.3] Relative opacity (more transparent): ${changes.barOpacity}`);
      return changes;
    }

    // Pattern 4: Named opacity levels
    const namedMatch = command.match(/(?:make\s+)?(?:bars?\s+)?(translucent|semi-transparent|faded|transparent|invisible|opaque|visible)/i);
    if (namedMatch) {
      const opacityName = namedMatch[1].toLowerCase();
      const opacityMap: Record<string, number> = {
        'invisible': 0,
        'transparent': 0.1,
        'translucent': 0.4,
        'faded': 0.5,
        'semi-transparent': 0.6,
        'visible': 1,
        'opaque': 1,
      };
      changes.barOpacity = opacityMap[opacityName] ?? 1;
      console.log(`[Phase 1.3] Named opacity "${opacityName}": ${changes.barOpacity}`);
      return changes;
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  // ============================================
  // Phase 1.4: Negative Value Color Commands
  // ============================================

  /**
   * Phase 1.4: Detect negative value color commands
   */
  private isNegativeColorCommand(command: string): boolean {
    return /negative|positive|below\s+zero|above\s+zero|red.*green|green.*red|color.*based|conditional.*color|same\s+color|uniform\s+color/.test(command);
  }

  /**
   * Phase 1.4: Parse negative value color commands
   */
  private parseNegativeColorCommand(command: string): Partial<BarChartProperties> | null {
    const changes: Partial<BarChartProperties> = {};

    console.log(`[Phase 1.4] Parsing negative color command: "${command}"`);

    // Pattern 1: Disable conditional coloring (check FIRST before enable patterns)
    if (/no\s+conditional/i.test(command) ||
        /disable\s+conditional/i.test(command) ||
        /same\s+color/i.test(command) ||
        /uniform\s+color/i.test(command)) {
      changes.useConditionalColors = false;
      console.log(`[Phase 1.4] Disabled conditional colors`);
      return changes;
    }

    // Pattern 2: Enable positive/negative coloring
    if (command.includes("red for negative") || command.includes("negative red") ||
        command.includes("positive green negative red") ||
        /enable\s+conditional/i.test(command) ||
        /conditional\s+color/i.test(command)) {
      changes.useConditionalColors = true;
      changes.positiveColor = '#10B981'; // Green
      changes.negativeColor = '#EF4444'; // Red
      console.log(`[Phase 1.4] Enabled conditional colors`);
      return changes;
    }

    // Pattern 3: Custom positive color
    const positiveMatch = command.match(/positive\s+(?:values?\s+)?(?:in\s+)?(#[0-9A-Fa-f]{3,6}|\w+)/i);
    if (positiveMatch) {
      changes.useConditionalColors = true;
      changes.positiveColor = positiveMatch[1];
      console.log(`[Phase 1.4] Positive color: ${changes.positiveColor}`);
    }

    // Pattern 4: Custom negative color
    const negativeMatch = command.match(/negative\s+(?:values?\s+)?(?:in\s+)?(#[0-9A-Fa-f]{3,6}|\w+)/i);
    if (negativeMatch) {
      changes.useConditionalColors = true;
      changes.negativeColor = negativeMatch[1];
      console.log(`[Phase 1.4] Negative color: ${changes.negativeColor}`);
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  /**
   * Get bar chart specific suggestions
   */
  getSuggestions(component: Component): string[] {
    const suggestions: string[] = [
      ...commonChartHandler.getSuggestions(component),
      ...axisMixin.getSuggestions(component),
      ...colorMixin.getSuggestions(component),
    ];

    // Bar-specific suggestions
    if (component.properties?.stacked) {
      suggestions.push("Unstack the bars", "Show as percentage stack");
    } else {
      suggestions.push("Stack the bars", "Group bars side by side");
    }

    if (component.properties?.layout !== "horizontal") {
      suggestions.push("Make horizontal bars");
    } else {
      suggestions.push("Make vertical bars");
    }

    suggestions.push(
      "Make bars wider",
      "Add rounded corners",
      "Show value labels on bars",
      "Reduce spacing between bars",
      "Add another data series",
      "Convert to waterfall chart"
    );

    // Phase 1 styling suggestions
    suggestions.push(
      "Use thin bars",
      "Make bars thick",
      "Set bar width to 40",
      "Add dashed bar borders",
      "Bar opacity 80%",
      "Make bars translucent",
      "Use red for negative values"
    );

    const rawBarKeys2 = component.properties?.barKeys;
    const barKeys = Array.isArray(rawBarKeys2)
      ? rawBarKeys2.filter((k): k is string => typeof k === 'string')
      : ["value"];
    if (barKeys.length === 1) {
      suggestions.push('Add series for "2023" data');
    }

    return suggestions;
  }
}

export const barChartHandler = new BarChartHandler();
